import React, { useState, useMemo } from 'react';
import ToolShell, { ACCENT, INK, fmt, Field, input, card, btn, printDoc } from '../tools/ToolShell';

/*
 * PUBLIC, no-login lead-magnet tool: /tools/brand-deal-contract-generator
 * Targets "influencer contract template" / "brand deal agreement india". Creator
 * fills the deal terms -> a plain-language collaboration agreement they can
 * download / print and send. Funnels to CreatorKhata (Contracts is a PRO feature).
 */

const num = (s) => parseFloat(String(s).replace(/[^0-9.]/g, '')) || 0;

export default function ContractToolPage() {
  const [creator, setCreator] = useState('');
  const [brand, setBrand] = useState('');
  const [deliverables, setDeliverables] = useState('');
  const [fee, setFee] = useState('');
  const [days, setDays] = useState('14');
  const [exclusivity, setExclusivity] = useState('30');

  const ready = useMemo(() => creator && brand && deliverables && num(fee) > 0, [creator, brand, deliverables, fee]);

  const contractHtml = () => {
    const today = new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
    const advance = Math.round(num(fee) * 0.5);
    const clause = (n, title, body) =>
      `<p style="margin:14px 0 4px;font-weight:700">${n}. ${title}</p><p style="margin:0;color:#333;line-height:1.55">${body}</p>`;
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Collaboration Agreement — ${creator} × ${brand}</title>
      <style>body{font-family:Georgia,'Times New Roman',serif;color:${INK};max-width:680px;margin:24px auto;padding:0 20px;font-size:14px}
      h1{font-size:20px;text-align:center;margin:0 0 4px}.muted{color:#888;font-size:12px;text-align:center}
      .sign{display:flex;justify-content:space-between;margin-top:48px}.sign div{width:45%;border-top:1px solid #333;padding-top:6px;font-size:12px;color:#555}
      .foot{margin-top:28px;font-size:11px;color:#aaa;text-align:center}@media print{.noprint{display:none}}</style></head>
      <body>
      <h1>Creator Collaboration Agreement</h1>
      <p class="muted">Dated ${today}</p>
      <p style="margin-top:18px;line-height:1.55">This agreement is made between <b>${creator}</b> ("the Creator") and <b>${brand}</b> ("the Brand") for the promotional collaboration described below.</p>
      ${clause(1, 'Deliverables', `The Creator will produce and publish: <b>${deliverables}</b>. Content will be original, created by the Creator, and posted on the Creator's own channels.`)}
      ${clause(2, 'Fee &amp; Payment', `The Brand will pay the Creator a total fee of <b>${fmt(num(fee))}</b> (plus GST if applicable). A 50% advance of <b>${fmt(advance)}</b> is payable to confirm this booking, with the balance due within <b>${days} days</b> of the content going live. Any TDS deducted will be deposited against the Creator's PAN and reflected on the invoice.`)}
      ${clause(3, 'Timeline', `The Creator will publish the agreed deliverables within <b>${days} days</b> of receiving the advance and all required brand materials/brief.`)}
      ${clause(4, 'Approvals &amp; Revisions', `The Brand may request one round of reasonable revisions before publishing. Editorial style and final creative control remain with the Creator to preserve authenticity with the audience. The content will be clearly marked as a paid partnership as required by ASCI guidelines.`)}
      ${clause(5, 'Usage Rights', `The Brand may reshare the content on its own social channels with credit. Any paid advertising use, or use beyond ${exclusivity} days, requires the Creator's written consent and may incur an additional usage fee.`)}
      ${clause(6, 'Exclusivity', `For <b>${exclusivity} days</b> from publishing, the Creator will not promote a directly competing product. No broader exclusivity applies.`)}
      ${clause(7, 'Cancellation', `If the Brand cancels after the advance is paid, the advance is non-refundable as compensation for reserved time. If the Creator cancels, the advance will be refunded in full.`)}
      <div class="sign"><div>${creator}<br><span style="color:#999">Creator</span></div><div>${brand}<br><span style="color:#999">Brand</span></div></div>
      <div class="foot">Generated free with CreatorKhata — creatorkhata.com. This is a plain-language template, not legal advice.</div>
      <button class="noprint" onclick="window.print()" style="margin-top:16px;background:${ACCENT};color:#fff;border:none;border-radius:8px;padding:10px 18px;font-weight:700;cursor:pointer;font-family:Arial">Print / Save as PDF</button>
      </body></html>`;
  };

  return (
    <ToolShell
      seo={{
        title: 'Free Brand Deal Contract Template Generator (India) — CreatorKhata',
        description: 'Free influencer contract generator. Fill in your deal terms and download a plain-language brand collaboration agreement covering fee, timeline, usage rights and exclusivity.',
        path: '/tools/brand-deal-contract-generator',
      }}
      h1="Brand Deal Contract Generator"
      sub="Protect yourself on every collaboration. Fill in the deal terms and download a clear creator–brand agreement covering payment, usage rights and exclusivity. No signup."
      cta={{
        heading: 'A handshake isn’t a contract.',
        body: 'CreatorKhata generates and stores your contracts alongside every deal, invoice and payment — so nothing slips through the cracks and you always have a paper trail.',
      }}
    >
      {/* Inputs */}
      <div style={card()}>
        <Field label="Your name / channel"><input style={input} value={creator} onChange={e => setCreator(e.target.value)} placeholder="e.g. Arjun Finance" /></Field>
        <Field label="Brand name"><input style={input} value={brand} onChange={e => setBrand(e.target.value)} placeholder="e.g. Acme India Pvt Ltd" /></Field>
        <Field label="Deliverables"><input style={input} value={deliverables} onChange={e => setDeliverables(e.target.value)} placeholder="1 dedicated YouTube video + 2 Stories" /></Field>
        <Field label="Total fee (₹)"><input style={input} type="number" value={fee} onChange={e => setFee(e.target.value)} placeholder="e.g. 80000" /></Field>
        <Field label="Payment / delivery window (days)"><input style={input} type="number" value={days} onChange={e => setDays(e.target.value)} placeholder="14" /></Field>
        <Field label="Exclusivity period (days)"><input style={input} type="number" value={exclusivity} onChange={e => setExclusivity(e.target.value)} placeholder="30" /></Field>
        <p style={{ fontSize: '0.72rem', color: '#aaa', margin: '0.25rem 0 0', lineHeight: 1.5 }}>
          A plain-language starting template (50% advance, one revision round, ASCI paid-partnership disclosure). Not legal advice — for high-value deals, have a lawyer review.
        </p>
      </div>

      {/* Preview */}
      <div style={card()}>
        {!ready ? (
          <div style={{ textAlign: 'center', color: '#aaa', padding: '2.5rem 1rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📄</div>
            <div style={{ marginBottom: '1.25rem' }}>
              Your contract builds here <b>automatically</b> as you fill the form — no submit button.<br />
              Add the <b style={{ color: ACCENT }}>names, deliverables and fee</b> to start.
            </div>
            <button disabled style={{ ...btn(ACCENT), width: '100%', opacity: 0.4, cursor: 'not-allowed' }}>⬇️ Download / Print Contract</button>
          </div>
        ) : (
          <div>
            <div style={{ borderBottom: `3px solid ${ACCENT}`, paddingBottom: '0.6rem', marginBottom: '0.6rem' }}>
              <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.15rem' }}>Collaboration Agreement</div>
              <div style={{ color: '#888', fontSize: '0.8rem' }}>{creator} × {brand}</div>
            </div>
            {[
              ['Deliverables', deliverables],
              ['Total fee', fmt(num(fee)) + ' (50% advance to confirm)'],
              ['Timeline', `Within ${days} days of advance`],
              ['Usage rights', `Organic reshare with credit; paid/extended use needs consent`],
              ['Exclusivity', `No competing brand for ${exclusivity} days`],
            ].map(([k, v]) => (
              <div key={k} style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
                <div style={{ fontSize: '0.72rem', color: '#999', fontWeight: 700, textTransform: 'uppercase' }}>{k}</div>
                <div style={{ fontSize: '0.88rem', color: '#333' }}>{v}</div>
              </div>
            ))}
            <button onClick={() => printDoc(contractHtml())} style={{ ...btn(ACCENT), width: '100%', marginTop: '0.9rem' }}>⬇️ Download / Print Contract</button>
          </div>
        )}
      </div>
    </ToolShell>
  );
}
